import { useEffect, useMemo, useCallback } from "react";

import { RECENT_DOMAINS_KEY } from "app/pages/digital-marketing/monitor-and-protect/services/domain-search";
import {
    useLimitedSetup,
    useLimits,
    calculateMaxKeywords,
} from "app/pages/digital-marketing/monitor-and-protect/shared/components/limits/use-limits";
import queryClient from "single-spa/react-app/query-client";
import { RecentService } from "app/services/recent/recentService";

import { steps } from "./types";
import { useBrandedKeywordsPrepopulation } from "./use-branded-keywords-prepopulation";
import { useCampaignName } from "./use-campaign-name";
import { useCountriesSelection } from "./use-countries-selection";
import { useKeywordsManagement } from "./use-keywords-management";
import { useWizardNavigation } from "./use-wizard-navigation";

export const useCampaignWizardState = (domain: string) => {
    const navigation = useWizardNavigation();
    const countries = useCountriesSelection();
    const keywords = useKeywordsManagement();
    const campaignName = useCampaignName();
    const balance = useLimits();

    const maxKeywordsAllowed = useMemo(() => {
        return calculateMaxKeywords(countries.selectedCountries.length, balance.available);
    }, [countries.selectedCountries.length, balance.available]);

    const brandedPrepopulation = useBrandedKeywordsPrepopulation({
        domain,
        selectedCountries: countries.selectedCountries,
        hasManualKeywords: keywords.hasManualKeywords,
        maxKeywordsAllowed,
        onKeywordsFetched: keywords.setManualKeywords,
    });

    const { isLimitReached, crawlCreditsUsed, formula } = useLimitedSetup({
        keywordsCount: keywords.deduplicatedKeywords.totalCount,
        locationsCount: countries.selectedCountries.length,
    });

    const resetState = useCallback(() => {
        navigation.resetNavigation();
        countries.resetCountries();
        keywords.resetKeywords();
        campaignName.resetCampaignName();
        brandedPrepopulation.resetPrepopulation();
    }, [navigation, countries, keywords, campaignName, brandedPrepopulation]);

    useEffect(() => {
        if (domain) {
            const addDomainToRecent = async () => {
                try {
                    const recentItem = {
                        type: "website" as const,
                        mainItem: domain,
                        comparedItems: [],
                        duration: "",
                        country: null,
                        comparedDuration: "",
                        pageId: {
                            section: "Ad Intelligence",
                            subSection: "Monitor & Protect",
                            subSubSection: "Homepage",
                        },
                    };
                    await RecentService.addRecent(recentItem);
                    await queryClient.invalidateQueries({ queryKey: RECENT_DOMAINS_KEY });
                } catch (error) {
                    console.error("Failed to add domain to recents:", error);
                }
            };

            void addDomainToRecent();
        }
    }, [domain]);

    return {
        state: {
            currentStep: navigation.currentStep,
            selectedCountries: countries.selectedCountries,
            campaignName: campaignName.campaignName,
        },

        keywords: {
            isPrepopulating: brandedPrepopulation.isPrepopulating,
            manual: keywords.manualKeywords,
            imports: keywords.importSources,
            deduplicated: keywords.deduplicatedKeywords,
            branded: brandedPrepopulation.brandedKeywords,
        },

        crawls: {
            balance,
            used: crawlCreditsUsed,
            isLimitReached,
            formula,
        },

        validation: {
            isCampaignNameValid: campaignName.isCampaignNameValid,
            isCrawlLimitValid: !isLimitReached,
        },

        steps: {
            current: navigation.currentStep,
            index: navigation.currentStepIndex + 1,
            total: navigation.totalSteps,
            config: navigation.getCurrentStepConfig(),
            constants: steps,
        },

        actions: {
            goToNext: navigation.goToNextStep,
            goToPrev: navigation.goToPrevStep,
            setStep: navigation.setCurrentStep,
            reset: resetState,

            setCountries: countries.setSelectedCountries,
            toggleCountry: countries.toggleCountry,
            setKeywords: keywords.setManualKeywords,
            addImport: keywords.addImportSource,
            removeImport: keywords.removeImportSource,
            setCampaignName: campaignName.setCampaignName,
            fetchBrandedKeywords: brandedPrepopulation.fetchBrandedKeywords,
        },
    };
};
